import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services';

@Component({
  selector: 'app-reset-password',
  standalone: false,
  templateUrl: './reset-password.html',
  styleUrls: ['./reset-password.css'],
})
export class ResetPasswordComponent implements OnInit {
  form!: FormGroup;
  resetToken = '';
  hasToken = false;
  submitting = false;
  resetSuccess = false;
  errorMessage = '';
  showNewPassword = false;
  showConfirmPassword = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {
    this.form = this.fb.group(
      {
        newPassword: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: ResetPasswordComponent.passwordsMatch },
    );
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const token = params.get('token')?.trim() ?? '';
      this.resetToken = token;
      this.hasToken = token.length > 0;
      this.cdr.detectChanges();
    });
  }

  private static passwordsMatch(group: AbstractControl): ValidationErrors | null {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    if (!newPassword || !confirmPassword) return null;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  onSubmit(): void {
    if (!this.hasToken) return;

    this.form.markAllAsTouched();
    this.errorMessage = '';

    if (this.form.invalid) return;

    this.submitting = true;
    const { newPassword, confirmPassword } = this.form.value;

    this.authService
      .resetPassword({
        token: this.resetToken,
        newPassword,
        confirmPassword,
      })
      .subscribe({
        next: () => {
          this.submitting = false;
          this.resetSuccess = true;
          this.cdr.detectChanges();
        },
        error: (err: { error?: { message?: string } }) => {
          this.submitting = false;
          this.errorMessage =
            err.error?.message ??
            'Unable to reset your password. The link may be invalid or expired.';
          this.cdr.detectChanges();
        },
      });
  }

  goToLogin(): void {
    void this.router.navigate(['/login']);
  }

  goToForgotPassword(): void {
    void this.router.navigate(['/login'], { queryParams: { forgot: '1' } });
  }

  get newPassword() {
    return this.form.get('newPassword');
  }

  get confirmPassword() {
    return this.form.get('confirmPassword');
  }

  get passwordsMismatch(): boolean {
    return (
      this.form.hasError('passwordMismatch') &&
      !!this.confirmPassword?.touched &&
      !!this.newPassword?.touched
    );
  }
}
